import http from '../../shared/http.js';
import { initFormModalLinks } from '../../shared/formModal.js';
import { initLiveList } from '../../shared/liveList.js';
import {
  confirmAction,
  hideLoading,
  notifyError,
  notifySuccess,
  showLoading
} from '../../shared/notify.js';

// The review queue for job ads (N08): three decisions, each one ending an ad's
// time in the queue.
//
// Two of them act straight away; "create as a new customer" needs an owner and
// a branch first, so it opens the dialog in pruefung.pug - one dialog for the
// page, filled in with whichever ad it was opened for, rather than one per card.

// What the two immediate decisions do, so the handler stays a lookup rather
// than a chain of ifs.
const ACTIONS = {
  zuordnen: {
    confirm: (button) => ({
      title: 'Inserat zuordnen',
      message: `Das Inserat ${button.dataset.kundeName} zuordnen?`,
      okText: 'Zuordnen'
    }),
    loading: 'Inserat wird zugeordnet …',
    done: 'Inserat wurde zugeordnet.',
    run: (id, button) =>
      http.patch(`/stelleninserate/${id}/zuordnen`, { kunde: button.dataset.kundeId }),
    // One company is one decision: the API carries it to that company's other
    // waiting ads, and saying how many is what makes the queue dropping by
    // more than one row read as intended rather than as a glitch.
    doneMit: (daten) => meldungMitWeiteren('Inserat wurde zugeordnet.', daten)
  },
  verwerfen: {
    confirm: () => ({
      title: 'Inserat verwerfen',
      message: 'Das Inserat wird verworfen und erscheint nicht mehr in der Prüfung.',
      okText: 'Verwerfen'
    }),
    loading: 'Inserat wird verworfen …',
    done: 'Inserat wurde verworfen.',
    run: (id) => http.patch(`/stelleninserate/${id}/verwerfen`)
  }
};

// The success message, naming the company's other ads the decision was carried
// to. `weitere` is how many rows moved besides the one that was clicked.
function meldungMitWeiteren(text, antwort) {
  // Axios wraps the body in `data`, and the API wraps the payload in `data`
  // again - so the count sits two levels down.
  const weitere = antwort?.data?.data?.weitere || 0;
  if (!weitere) return text;

  return weitere === 1
    ? `${text} Ein weiteres Inserat derselben Firma wurde mitzugeordnet.`
    : `${text} ${weitere} weitere Inserate derselben Firma wurden mitzugeordnet.`;
}

// The message to show when a decision fails. The API's own text is worth
// showing verbatim for the two cases a reviewer can act on: somebody else
// decided this ad first (409), and the company already exists under a UID the
// matching did not surface (409 from the create).
function fehlermeldung(err) {
  return err.response?.data?.message || 'Etwas ist schiefgelaufen. Bitte versuchen Sie es erneut.';
}

export function initInseratPruefung() {
  const live = initLiveList({
    results: 'inserat-list',
    path: '/inserate',
    forms: ['inserat-filter', 'inserat-search']
  });
  if (!live) return;

  // "Job erstellen" opens the job form in the shared dialog, prefilled from
  // the ad. The selector is narrow on purpose: the cards also link to
  // customers and jobs, and only this one is a form.
  //
  // No onSaved refresh - the job form navigates to the new job on success,
  // which is where the person is going next anyway.
  initFormModalLinks({ selector: 'a[href^="/jobs/new?inserat="]' });

  const list = document.getElementById('inserat-list');
  const dialog = document.getElementById('inserat-kunde-dialog');
  const form = document.getElementById('inserat-kunde-form');
  const firmaAnzeige = document.getElementById('inserat-kunde-firma');

  // Which ad the dialog is currently about. Held here rather than in a data
  // attribute on the dialog, because the card it belongs to can be replaced by
  // a refresh while the dialog is open.
  let offenesInserat = null;

  // One delegated listener for the whole region, so the cards that arrive with
  // each refreshed fragment are live without rebinding anything.
  list.addEventListener('click', async (event) => {
    const button = event.target.closest('[data-action]');
    if (!button) return;

    const karte = button.closest('[data-inserat-id]');
    if (!karte) return;
    const { inseratId } = karte.dataset;

    if (button.dataset.action === 'kunde-anlegen') {
      offenesInserat = inseratId;
      firmaAnzeige.textContent = button.dataset.firma;
      form.reset();
      dialog.showModal();
      return;
    }

    const action = ACTIONS[button.dataset.action];
    if (!action) return;

    if (action.confirm && !(await confirmAction(action.confirm(button)))) return;

    button.disabled = true;
    showLoading(action.loading);

    try {
      const antwort = await action.run(inseratId, button);
      // Refetch rather than reload: the queue redraws without the decided ad
      // while the filter, the search and the scroll position all stay put.
      await live.refresh();
      hideLoading();
      notifySuccess(action.doneMit ? action.doneMit(antwort) : action.done);
    } catch (err) {
      hideLoading();
      button.disabled = false;
      notifyError(fehlermeldung(err));
      // A 409 means somebody else decided this ad first, so the card on screen
      // is out of date. Refetching is what makes that visible rather than
      // leaving a button that will fail again.
      if (err.response?.status === 409) await live.refresh();
    }
  });

  // The dialog's own buttons. The cancel button is type=button inside a
  // method=dialog form, so it needs closing explicitly.
  dialog.addEventListener('click', (event) => {
    if (event.target.closest('[data-action="dialog-abbrechen"]')) dialog.close();
  });

  form.addEventListener('submit', async (event) => {
    // The form is method=dialog, which would close the dialog and discard the
    // submission before the request is made.
    event.preventDefault();
    if (!offenesInserat) return;

    const daten = Object.fromEntries(new FormData(form).entries());
    if (!daten.verantwortlichBenutzer || !daten.filiale) {
      notifyError('Bitte Personalberater und Filiale wählen.');
      return;
    }

    showLoading('Kunde wird angelegt …');

    try {
      await http.post(`/stelleninserate/${offenesInserat}/kunde`, daten);
      dialog.close();
      offenesInserat = null;
      await live.refresh();
      hideLoading();
      notifySuccess('Kunde wurde angelegt und das Inserat zugeordnet.');
    } catch (err) {
      hideLoading();
      notifyError(fehlermeldung(err));
      if (err.response?.status === 409) {
        dialog.close();
        offenesInserat = null;
        await live.refresh();
      }
    }
  });
}
